package fyi.moko.hotpl.ui.theme

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * The recurring "second sheet of paper" depth cue from the Stitch mockups (Friends
 * invite card precedent, Home neighborhood rows, Detail's newest visit-history entry):
 * an offset rounded rect drawn behind this composable's own bounds. Must come before
 * `.clip()`/`.background()` in the modifier chain so the offset portion isn't clipped
 * away by the card's own shape.
 */
fun Modifier.stackedPaper(
    offset: Dp = 5.dp,
    cornerRadius: Dp = 12.dp,
    fillColor: Color? = null,
    borderColor: Color? = null,
): Modifier = composed {
    val fill = fillColor ?: MaterialTheme.colorScheme.surfaceContainerLow
    val border = borderColor ?: MaterialTheme.colorScheme.outlineVariant
    drawBehind {
        val offsetPx = offset.toPx()
        val radius = CornerRadius(cornerRadius.toPx())
        val topLeft = Offset(offsetPx, offsetPx)
        drawRoundRect(color = fill, topLeft = topLeft, size = size, cornerRadius = radius)
        drawRoundRect(
            color = border,
            topLeft = topLeft,
            size = size,
            cornerRadius = radius,
            style = Stroke(width = 1.dp.toPx()),
        )
    }
}

/**
 * Moss-tinted soft shadow (DESIGN.md: "always tint shadows with the Secondary (Moss)
 * color") in place of Material's default black-alpha shadow, used on the invite-code
 * card, Home's paper cards, and the Detail visibility card.
 */
fun Modifier.mossShadow(
    elevation: Dp = 8.dp,
    shape: Shape = RoundedCornerShape(8.dp),
    alpha: Float = 0.18f,
): Modifier = composed {
    val moss = MaterialTheme.colorScheme.secondary
    shadow(
        elevation = elevation,
        shape = shape,
        ambientColor = moss.copy(alpha = alpha),
        spotColor = moss.copy(alpha = alpha),
    )
}

/**
 * Home "Your Neighborhood" row's warmth indicator — a row of small dots, filled up to
 * [count] (visit frequency), outline for the remainder up to [max].
 */
@Composable
fun WarmthDots(
    count: Int,
    modifier: Modifier = Modifier,
    max: Int = 4,
    dotSize: Dp = 6.dp,
    filledColor: Color = Color(0xFFD97706),
) {
    Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(2.dp)) {
        repeat(max) { index ->
            if (index < count) {
                Box(Modifier.size(dotSize).clip(CircleShape).background(filledColor))
            } else {
                Box(
                    Modifier.size(dotSize)
                        .clip(CircleShape)
                        .border(1.dp, MaterialTheme.colorScheme.outlineVariant, CircleShape),
                )
            }
        }
    }
}

/**
 * Detail screen's "Revisit Rhythm" progress bar — a rounded track with an amber fill
 * sized to [fraction] (0f..1f). The mockup's shimmer sweep animation is skipped (a
 * decorative nice-to-have, not core to the warmth signal it conveys).
 */
@Composable
fun WarmthBar(
    fraction: Float,
    modifier: Modifier = Modifier,
    trackHeight: Dp = 12.dp,
) {
    Box(
        modifier
            .height(trackHeight)
            .clip(RoundedCornerShape(50))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.8f)),
    ) {
        Box(
            Modifier
                .fillMaxWidth(fraction.coerceIn(0f, 1f))
                .height(trackHeight)
                .clip(RoundedCornerShape(50))
                .background(MaterialTheme.colorScheme.primary),
        )
    }
}
